import type { IDashboardColumn } from 'models';

/**
 * Sort confirmation dialog (§3.J) — TEMPORARY/minimal markup for 2b (full modal
 * polish + Esc/Enter global handlers are 2c). Confirming persists
 * user.dashboard_projects_default_order and re-fetches server-side.
 *
 * Field-type-aware copy: name = "Alphanumerically, 0 to Z / Z to 0"; date fields
 * = "Date and Time, Old to New / New to Old"; task = "Chronologically by Order";
 * sell_to_customer = "Small to Large / Large to Small" (parity 41).
 */
interface SortConfirmProps {
  field: string;
  /** the order that WOULD apply on confirm. */
  nextOrder: 'asc' | 'desc';
  columns: IDashboardColumn[] | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

const DATE_FIELDS = new Set(['create_date', 'created_on_date', 'actual_ship', 'scheduled_ship']);

/** Human label for the field being sorted. */
function fieldLabel(field: string, columns: IDashboardColumn[] | undefined): string {
  if (field === 'name') return 'Project Name';
  if (field === 'create_date' || field === 'created_on_date') return 'Created on';
  return columns?.find((c) => c.field_name === field)?.name ?? field;
}

/** Field-type-aware {type, order} copy. */
function describe(field: string, order: 'asc' | 'desc'): { type: string; order: string } {
  if (DATE_FIELDS.has(field)) {
    return { type: 'Date and Time,', order: order === 'asc' ? 'Old to New' : 'New to Old' };
  }
  if (field === 'task') {
    return { type: 'Chronologically by Order', order: '' };
  }
  if (field === 'sell_to_customer') {
    return { type: ' ', order: order === 'asc' ? 'Small to Large' : 'Large to Small' };
  }
  return { type: 'Alphanumerically,', order: order === 'asc' ? '0 to Z' : 'Z to 0' };
}

const SortConfirm = ({ field, nextOrder, columns, onConfirm, onCancel }: SortConfirmProps) => {
  const sortByName = fieldLabel(field, columns);
  const { type, order } = describe(field, nextOrder);

  return (
    <div id="popup-content" className="popup-wrapper width-small sort-popup-content active">
      <div className="popup-content">
        <h2 className="popup-heading">Sort Project List?</h2>
        <div className="popup-content-part">
          <p>
            Please confirm that the project list in the Dashboard will be sorted. This action is
            not reversible.
          </p>
          <p style={{ fontWeight: 600 }}>
            Sort by {sortByName}, {type} {order}
          </p>
        </div>
        <div className="popup-control-buttons">
          <button
            name="confirm_sort"
            className="highlighted-btn"
            type="button"
            autoFocus
            onClick={onConfirm}
          >
            Sort
          </button>
          <button name="cancel_sort" className="not-highlighted-btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SortConfirm;
